import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes@0.4.6';

import '@/i18n';
import '@/index.css';

import { initUrlParams } from '@/utils/urlParams';
import { Toaster } from '@/components/ui/sonner';
import { AuthInitializer } from '@/components/AuthInitializer';
import { LoginPage } from '@/components/LoginPage';
import { SignupPage } from '@/components/SignupPage';
import App from '@/App';

initUrlParams();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

const appElement = <App />;

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <BrowserRouter>
          <AuthInitializer>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/" element={appElement} />
              <Route path="/dashboard" element={appElement} />
              <Route path="/chat" element={appElement} />
              <Route path="/cowork" element={appElement} />
              <Route path="/messages" element={appElement} />
              <Route path="/brand-pulse" element={appElement} />
              <Route path="/content-hub" element={appElement} />
              <Route path="/creators" element={appElement} />
              <Route path="/creators/search" element={appElement} />
              <Route path="/creators/lists/ambassadors" element={appElement} />
              <Route path="/creators/creatorlist/:listId" element={appElement} />
              <Route path="/creators/:creatorId" element={appElement} />
              <Route path="/creator-lists" element={appElement} />
              <Route path="/creator-lists/:listId" element={appElement} />
              <Route path="/audience-insights" element={appElement} />
              <Route path="/trends" element={appElement} />
              <Route path="/news-monitor" element={appElement} />
              <Route path="/ad-studio" element={appElement} />
              <Route path="/ad-studio/video" element={appElement} />
              <Route path="/ad-studio/dubbing" element={appElement} />
              <Route path="/campaigns" element={appElement} />
              <Route path="/campaigns/calendar" element={appElement} />
              <Route path="/campaigns/new" element={appElement} />
              <Route path="/campaigns/:campaignId" element={appElement} />
              <Route path="/campaigns/:campaignId/summary/report" element={appElement} />
              <Route path="/campaigns/:campaignId/:subTab/beat/:beatId" element={appElement} />
              <Route path="/campaigns/:campaignId/:subTab" element={appElement} />
              <Route path="/ambassador-hub" element={appElement} />
              <Route path="/drafts" element={appElement} />
              <Route path="/casting" element={appElement} />
              <Route path="/strategies" element={appElement} />
              <Route path="/performance" element={appElement} />
              <Route path="/brand-lift" element={appElement} />
              <Route path="/marketing-mix-model" element={appElement} />
              <Route path="/awards" element={appElement} />
              <Route path="/awards/:trophyId" element={appElement} />
              <Route path="/settings" element={appElement} />
              <Route path="/team" element={appElement} />
              <Route path="/dev-mode" element={appElement} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthInitializer>
          <Toaster />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
